import { getCurrentAccessToken, getCurrentUsername, getCurrentUserId } from '@/utils/auth';
import { hasValue, hasStringValue, nullToString } from '@/utils/validation';
import { showToast } from '@/contexts/ToastContext';
import { ServerErrors } from '@/models/errors';
import { getCurrentFrontendVersion } from './version-service';

const KST_LOGIN_TYPE_HEADER_VALUE = 'orna_client_portal';

interface ApiErrorResult {
  status: number;
  statusText: string;
  responseText: string;
  responseJSON: any;
}

class API {
  baseURL: string;
  private api: string = '';
  iosDeviceTokenEndpoint: string | null = null;
  androidDeviceTokenEndpoint: string | null = null;
  private apiEnabled: boolean = true;
  private backendVersion: number | null = null;
  private jsErrorMap: Record<string, number> = {};
  private onUnauthorized: (() => void) | null = null;
  private apiReadyPromise: Promise<void> = Promise.resolve();

  constructor() {
    this.baseURL = 'https://orna-prod.kerkton.com:8443';
  }

  setOnUnauthorized(callback: () => void) {
    this.onUnauthorized = callback;
  }

  setApiReadyPromise(promise: Promise<void>) {
    this.apiReadyPromise = promise;
  }

  setCurrentBackendVersion(version: number) {
    this.backendVersion = version;
    this.api = `/api/v${version}/`;
    this.iosDeviceTokenEndpoint = this.api + 'devicetoken/ios';
    this.androidDeviceTokenEndpoint = this.api + 'devicetoken/android';
  }

  getCurrentBackendVersion(): number | null {
    return this.backendVersion;
  }

  setAPIEnabled(isEnabled: boolean) {
    this.apiEnabled = isEnabled;
  }

  isAPIEnabled(): boolean {
    return this.apiEnabled;
  }

  // ── Auth endpoints ──────────────────────────────────────────────────

  login(username: string, password: string): Promise<any> {
    return this.ajaxCall({
      path: 'login',
      method: 'POST',
      body: { username, password },
      allowHttpCode403Forbidden: true,
      isLogin: true,
    });
  }

  logout(): Promise<any> {
    return this.ajaxCall({
      path: 'logout',
      method: 'POST',
    });
  }

  // ── Password / Registration ─────────────────────────────────────────

  passwordResetByUsername(data: { username: string }): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/password/reset/send',
      method: 'POST',
      body: data,
      noAuthToken: true,
    });
  }

  resetPassword(data: any): Promise<any> {
    return this.ajaxCall({
      path: 'password/reset/update',
      method: 'PUT',
      body: data,
      noAuthToken: true,
    });
  }

  getUsernameForRegistration(registrationCode: string): Promise<any> {
    return this.ajaxCall({
      path: 'registration/username/' + registrationCode,
      method: 'GET',
    });
  }

  getUsernameForPasswordReset(passwordResetCode: string): Promise<any> {
    return this.ajaxCall({
      path: 'password/reset/username/' + passwordResetCode,
      method: 'GET',
    });
  }

  register(data: any): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/registration/register',
      method: 'POST',
      body: data,
    });
  }

  // ── Company ─────────────────────────────────────────────────────────

  selectCompany(companyId: number): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/company/select',
      method: 'POST',
      body: { companyId },
    });
  }

  // ── Reports ─────────────────────────────────────────────────────────

  getActivityReportPdfBase64(data: any): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/activityreport/pdf',
      method: 'POST',
      body: data,
    });
  }

  callCardSearch(start: number, perPage: number, data: any): Promise<any> {
    return this.ajaxCall({
      path: `clientportal/callcard/search/all/?start=${start}&perPage=${perPage}`,
      method: 'POST',
      body: data,
    });
  }

  getCallCardPdfBase64(data: any): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/callcard/pdf',
      method: 'POST',
      body: data,
    });
  }

  caseReportSearch(start: number, perPage: number, data: any): Promise<any> {
    return this.ajaxCall({
      path: `clientportal/report/search/all/?start=${start}&perPage=${perPage}`,
      method: 'POST',
      body: data,
    });
  }

  getCaseReportPdfUrl(reportId: number): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/report/pdf/url',
      method: 'POST',
      body: { reportId },
    });
  }

  getAnalyticsReportPdfBase64(data: any): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/analytics/report/pdf',
      method: 'POST',
      body: data,
    });
  }

  analyticsReportClientStartDates(): Promise<any> {
    return this.ajaxCall({
      path: 'clientportal/analytics/report/clientstartdates/get',
      method: 'GET',
    });
  }

  // ── Version checks ──────────────────────────────────────────────────

  async frontendVersionCheck(): Promise<string> {
    const response = await fetch('../Content/orna-version.js');
    return response.text();
  }

  async backendVersionCheck(): Promise<string> {
    const response = await fetch('https://orna.kerkton.com/Content/orna-backend-version.js');
    if (!response.ok) throw new Error('Backend version check failed');
    return response.text();
  }

  // ── Error logging ───────────────────────────────────────────────────

  insertNewJSError(fullError: string, url: string, errorMessage: string | null, type: string): void {
    if (location.hostname === 'localhost' || location.hostname === '127.0.0.1') {
      if (type === 'UNCAUGHT') {
        console.error('Orna JavaScript error:', fullError);
      }
      return;
    }

    const body: any = {
      error: nullToString(fullError) + '<br />window.location.href: ' + window.location.href +
        ' -- username: ' + getCurrentUsername() + ' -- user id: ' + getCurrentUserId(),
      url: nullToString(url),
      errorMessage: nullToString(errorMessage),
      type: nullToString(type),
      frontEndVersion: hasValue(getCurrentFrontendVersion()) ? getCurrentFrontendVersion()!.toString() : 'Error',
      userAgent: window.navigator.userAgent,
    };

    const stringifiedBody = JSON.stringify(body);
    if (this.jsErrorMap[stringifiedBody]) {
      this.jsErrorMap[stringifiedBody]++;
    } else {
      this.jsErrorMap[stringifiedBody] = 1;
    }
    if (this.jsErrorMap[stringifiedBody] > 3) return;

    this.ajaxCall({
      path: 'jserror/log',
      method: 'POST',
      body: JSON.parse(stringifiedBody),
      authTokenRefresh: false,
    }).catch(() => {});
  }

  // ── Core fetch wrapper ──────────────────────────────────────────────

  private async ajaxCall(params: {
    path: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: any;
    noAuthToken?: boolean;
    isLogin?: boolean;
    allowHttpCode401Unauthorized?: boolean;
    allowHttpCode403Forbidden?: boolean;
    authTokenRefresh?: boolean;
  }): Promise<any> {
    await this.apiReadyPromise;

    if (!this.apiEnabled) {
      return Promise.reject(new Error('API disabled'));
    }

    const {
      path, method, body, noAuthToken, isLogin,
      allowHttpCode401Unauthorized, allowHttpCode403Forbidden,
      authTokenRefresh,
    } = params;
    const url = this.api + path;

    const headers: Record<string, string> = {};

    const accessToken = noAuthToken ? null : getCurrentAccessToken();
    if (hasValue(accessToken)) {
      headers['X-Auth-Token'] = accessToken!;
    }
    if (authTokenRefresh === false) {
      headers['X-Auth-Token-Refresh'] = 'false';
    }
    headers['Accept'] = 'application/json; charset=utf-8';
    if (method === 'POST' || method === 'PUT') {
      headers['Content-Type'] = 'application/json; charset=utf-8';
    }
    if (isLogin) {
      headers['KST-Login-Type'] = KST_LOGIN_TYPE_HEADER_VALUE;
    }

    const fetchOptions: RequestInit = { method, headers };
    if (body) {
      fetchOptions.body = JSON.stringify(body);
    }

    try {
      const response = await fetch(this.baseURL + url, fetchOptions);
      const responseText = await response.text();
      let responseJSON: any;
      try { responseJSON = JSON.parse(responseText); } catch { responseJSON = undefined; }

      if (!this.apiEnabled) throw new Error('API disabled');

      if (response.ok) {
        return responseJSON;
      }

      // Error path
      const result: ApiErrorResult = {
        status: response.status,
        statusText: response.statusText,
        responseText,
        responseJSON,
      };

      if (responseText === '{"error":"Internal Server Error"}') {
        const serverErrors = new ServerErrors(responseText, 1);
        serverErrors.displayErrors();
        throw result;
      }

      const isUnauthorizedError = (r: ApiErrorResult) =>
        r.statusText === 'Unauthorized' || r.status === 401 || r.responseText === '{"error":"unauthorized"}';
      const isForbiddenError = (r: ApiErrorResult) =>
        r.statusText === 'Forbidden' || r.status === 403 || r.responseText === '{"error":"forbidden"}';

      if (isUnauthorizedError(result)) {
        if (!allowHttpCode401Unauthorized && this.onUnauthorized) {
          this.onUnauthorized();
        }
      } else if (isForbiddenError(result)) {
        if (!allowHttpCode403Forbidden && this.onUnauthorized) {
          this.onUnauthorized();
        }
      } else if (result.status === 500) {
        const serverErrors = new ServerErrors('Internal Server Error', 1);
        serverErrors.displayErrors();
      } else if (result.responseJSON?.error === 'access denied') {
        ServerErrors.displayError(result.responseJSON.error);
      } else {
        if (!result.responseText) {
          if (result.status === 0) {
            showToast('Bad Connection.', 'error');
          } else {
            this.insertNewJSError(`result: ${JSON.stringify(result)}`, url, null, 'CAUGHT');
          }
        } else {
          try {
            const parsed = JSON.parse(result.responseText);
            const serverErrors = new ServerErrors(
              parsed.errors?._embedded ?? parsed,
              parsed.errors?.total
            );
            serverErrors.displayErrors();
          } catch {
            this.insertNewJSError(`result.responseText: ${result.responseText}`, url, null, 'CAUGHT');
          }
        }
      }

      throw result;
    } catch (error: any) {
      if (!this.apiEnabled) throw error;
      if (error?.status) throw error; // Already handled ApiErrorResult
      this.insertNewJSError('Network error', url, null, 'CAUGHT');
      throw error;
    }
  }
}

export const api = new API();
